/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: modules\pbxCli\CommandSlotButton.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the PBX Tools module.
----------------------------------------------------------------*/

import { useCallback } from 'react';
import { Edit } from 'lucide-react';
import { Button } from '../../components/ui/button';
import SimpleTooltip from '../../components/ui/SimpleTooltip';

interface CommandSlotButtonProps {
  slotIndex: number;
  isConfigured: boolean;
  label: string;
  tooltip: string;
  command?: string;
  disabled: boolean;
  editLabel: string;
  onExecute: (command: string) => void;
  onEdit: (slotIndex: number) => void;
}

export default function CommandSlotButton({
  slotIndex,
  isConfigured,
  label,
  tooltip,
  command,
  disabled,
  editLabel,
  onExecute,
  onEdit,
}: CommandSlotButtonProps) {
  const handlePrimaryClick = useCallback(() => {
    if (isConfigured && command) {
      onExecute(command);
      return;
    }

    onEdit(slotIndex);
  }, [command, isConfigured, onEdit, onExecute, slotIndex]);

  const handleEditClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();
      onEdit(slotIndex);
    },
    [onEdit, slotIndex],
  );

  return (
    <div className="grid w-full grid-cols-1 gap-2 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center">
      <SimpleTooltip content={tooltip} position="top" maxWidth="320px">
        <Button
          type="button"
          variant={isConfigured ? 'secondary' : 'outline'}
          size="sm"
          onClick={handlePrimaryClick}
          disabled={disabled}
          className={`w-full truncate text-left text-xs sm:text-sm ${
            isConfigured ? '' : 'border-dashed opacity-60'
          }`}
        >
          <span className="block truncate">{label}</span>
        </Button>
      </SimpleTooltip>

      <SimpleTooltip content={editLabel} position="top">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleEditClick}
          className="flex w-full items-center justify-center rounded-md p-0 opacity-80 transition-opacity hover:opacity-100 sm:h-9 sm:w-9"
          aria-label={editLabel}
        >
          <Edit className="h-4 w-4" />
        </Button>
      </SimpleTooltip>
    </div>
  );
}
